import { ApplicationRef, ComponentRef, createComponent, EnvironmentInjector, inject, Injectable, Injector } from '@angular/core';
import { ToastComponent } from '../components/toast/toast';
import { LoaderComponent } from '../components/loader/loader.component';


@Injectable({
  providedIn: 'root'
})
export class SharedCommonService {

  constructor() { 
  }
    private loaderRef?: ComponentRef<LoaderComponent> | null = null;
    private toastRef?: ComponentRef<ToastComponent> | null = null;
    private readonly envInjector = inject(EnvironmentInjector); 
    private readonly injector = inject(Injector);
    private readonly appRef = inject(ApplicationRef);


    /**
   * Checks if a specific control in a form has a particular validation error.
   *
   * @param form - The FormGroup containing the control to check.
   * @param controlName - The name of the control to check for errors.
   * @param validationType - The type of validation error to check for.
   * @returns `true` if the control has the specified validation error and is either dirty or touched, otherwise `false`.
   */
    isControlHasError(form: any, controlName: string, validationType: string): boolean {
      const control = form.controls[controlName];
      if (!control) {
        return false;
      }
      return control.hasError(validationType) && (control.dirty || control.touched);
    }
  

  //      *
  //  * @param type - The type of the toast notification. Can be either 'success' or 'danger'.
  //  * @param title - The title text to display in the toast notification.
  //  * @param subtitle - The subtitle text to display in the toast notification.
  //  * @param timeout - The duration (in milliseconds) for which the toast should be displayed. Defaults to 3000ms.
  //  */
    showToast(type: 'success' | 'danger' | 'warning', title: string, timeout: number = 15000 ): void {
      // Destroy previous toast if it exists
      this.destroyExistingToast();

      this.toastRef = createComponent(ToastComponent, {
        environmentInjector: this.envInjector,
        elementInjector: this.injector,
      });

      this.appRef.attachView(this.toastRef.hostView);
      const domElem = (this.toastRef.hostView as any).rootNodes[0] as HTMLElement;

      document.body.appendChild(domElem);

      this.toastRef.instance.triggerToast(type, title, timeout);

      this.toastRef.instance.toastClosed.subscribe(() => {
        if (this.toastRef) {
          this.appRef.detachView(this.toastRef.hostView);
          this.toastRef.destroy();
          this.toastRef = null;
        }
      });
}

  /**
   * Destroys the existing toast if one is currently displayed.
   * This method is called internally before showing a new toast to ensure
   * only one toast is displayed at a time.
   */
  private destroyExistingToast(): void {
    if (this.toastRef) {
      this.appRef.detachView(this.toastRef.hostView);
      this.toastRef.destroy();
      this.toastRef = null;
    }
  }

  /**
   * Manually hides and destroys the current toast if one exists.
   * This is a public method that can be called to programmatically dismiss toasts.
   */
  hideToast(): void {
    this.destroyExistingToast();
  }

 /**
   * Displays a loader component on the screen. This method ensures that the loader
   * is created and attached to the DOM only once. If the loader is already present,
   * it does nothing. The loader component is dynamically created and appended to
   * the document body.
   *
   * @param message - Optional custom message to display with the loader
   *
   * @remarks
   * - The loader component is created using Angular's `createComponent` method.
   * - The loader is attached to the application view and displayed after being fully initialized.
   *
   * @returns void
   */
  showLoader(message?: string): void {
    if (!this.loaderRef) {
      this.loaderRef = createComponent(LoaderComponent, {
        environmentInjector: this.envInjector,
        elementInjector: this.injector,
      });

      this.appRef.attachView(this.loaderRef.hostView);
      const domElem = (this.loaderRef.hostView as any).rootNodes[0] as HTMLElement;
      document.body.appendChild(domElem);
      // Call show after fully attached
      this.loaderRef.instance.show(message);
    } else if (message) {
      // If loader already exists but we have a new message, update it
      this.loaderRef.instance.show(message);
    }
  }

  /**
   * Hides and destroys the loader component if it exists.
   * 
   * This method checks if a loader reference (`loaderRef`) is present. If so, it detaches the loader's view 
   * from the Angular application reference (`appRef`), destroys the loader component, and sets the 
   * `loaderRef` to `null` to clean up resources.
   */
  hideLoader(): void {
    if (this.loaderRef) {
      this.appRef.detachView(this.loaderRef.hostView);
      this.loaderRef.destroy();
      this.loaderRef = null;
    }
  }

}
